import { Button, Spinner } from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';
import useAppSelector from 'hooks/shared/store/useAppSelector';
import useAppDispatch from 'hooks/shared/store/useAppDispatch';
import { authSelector } from 'selectors/modules/auth';
import { authSlice } from 'slices/modules/auth/authSlice';
import { getScorLocale, scorT } from 'i18n/scorLocale';
import { fetchScorReportFile, ScorFileError } from './scorReportFile';
import type { ScorPdfContentProps } from './ScorPdfContent';
import styles from './scor.module.scss';

// Only preload viewer code. Protected report bytes are fetched on opening.
export const preloadScorPdfViewer = async (): Promise<typeof import('./ScorPdfContent')> => await import('./ScorPdfContent');

const PdfFile: FC<{ file: ApiScorReportFile }> = ({ file }) => {
  const auth = useAppSelector(authSelector);
  const dispatch = useAppDispatch();
  const [loaded, setLoaded] = useState<{ bytes: Uint8Array; Content: FC<ScorPdfContentProps> }>();
  const [error, setError] = useState<ScorFileError>();
  const [downloaded, setDownloaded] = useState(0);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoaded(undefined); setError(undefined); setDownloaded(0);
    let timer: number;
    // A large file may take minutes. Only stop when no data arrives for a minute.
    const onProgress = (bytes: number): void => {
      if (controller.signal.aborted) return;
      setDownloaded(bytes);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        controller.abort();
        setError(new ScorFileError(0, undefined, scorT('fileLoadStalled')));
      }, 60000);
    };
    onProgress(0);
    void Promise.all([fetchScorReportFile(file.url, auth, controller.signal, onProgress), preloadScorPdfViewer()]).then(([bytes, viewer]) => {
      if (!controller.signal.aborted) setLoaded({ bytes, Content: viewer.default });
    }).catch((reason) => {
      if (controller.signal.aborted) return;
      if (reason instanceof ScorFileError && reason.code === 'SESSION_UUID_NOT_FOUND') dispatch(authSlice.actions.logout());
      controller.abort();
      setError(reason instanceof ScorFileError ? reason : new ScorFileError(0));
    }).finally(() => window.clearTimeout(timer));

    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [file.url, auth, attempt, dispatch]);

  return <div className={styles.pdfViewer}>
    {error ? <div role={error.code === 'REPORT_NOT_READY' ? 'status' : 'alert'}>
      <p>{error.message}</p>
      <Button onClick={() => setAttempt((value) => value + 1)}>{scorT('retryFile')}</Button>
    </div> : !loaded ? <div className={styles.empty} role="status">
      <Spinner />
      <p>{downloaded > 0 ? scorT('fileDownloaded', { size: new Intl.NumberFormat(getScorLocale(), { style: 'unit', unit: 'megabyte', maximumFractionDigits: 2 }).format(downloaded / 1048576) }) : scorT('fileLoading')}</p>
    </div> : <loaded.Content bytes={loaded.bytes} onError={() => setError(new ScorFileError(0))} />}
  </div>;
};

const ScorPdfViewer: FC<{ file: ApiScorReportFile }> = ({ file }) => <PdfFile key={file.url} file={file} />;

export default ScorPdfViewer;
